import { Button, Typography } from "@procore/core-react";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import NoSearchResultsMobileIcon from "media/icons/noSearchResultsMobile.svg?react";
import NoSearchResultsWebIcon from "media/icons/noSearchResultsWeb.svg?react";
import { selectIsProcoreMobileDesign } from "slices/explorer/selectors";

const ILLUSTRATION_HEIGHT_THRESHOLD = 300;

interface NoSearchResultsProps {
    onClearSearch: () => void;
}

const containerStyle: CSSProperties = {
    height: "100%",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "safe center",
    textAlign: "center",
    padding: "32px 13px",
    gap: 16,
};

export function NoSearchResults({ onClearSearch }: NoSearchResultsProps) {
    const { t } = useTranslation();
    const isMobile = useAppSelector(selectIsProcoreMobileDesign);
    const Illustration = isMobile ? NoSearchResultsMobileIcon : NoSearchResultsWebIcon;

    const containerRef = useRef<HTMLDivElement>(null);
    const [showIllustration, setShowIllustration] = useState(true);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }

        const observer = new ResizeObserver(([entry]) => {
            setShowIllustration(entry.contentRect.height >= ILLUSTRATION_HEIGHT_THRESHOLD);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef} role="status" aria-live="polite" style={containerStyle}>
            {showIllustration && <Illustration aria-hidden="true" />}
            {isMobile ? (
                <Typography style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.3 }}>
                    {t("noSheetsMatchSearch")}
                </Typography>
            ) : (
                <Typography intent="h3">{t("noSheetsMatchSearch")}</Typography>
            )}
            <Typography intent={isMobile ? "small" : "body"} color="gray45" style={{ lineHeight: 1.5 }}>
                {t("noSheetsMatchSearchHint")}
            </Typography>
            <Button
                variant="primary"
                onClick={onClearSearch}
                style={
                    isMobile
                        ? { backgroundColor: "rgba(23, 26, 28, 1)", borderColor: "rgba(23, 26, 28, 1)" }
                        : undefined
                }
            >
                {t("clearSearch")}
            </Button>
        </div>
    );
}
